import { Injectable } from '@angular/core';
import { ISelectOptionGroup } from '../../models/select-option-group';
import { BehaviorSubject, Observable, of, throwError, timer } from 'rxjs';
import { ISelectOption } from '../../models/select-option';
import { ReferenceDataService } from '../../../reference-data/reference-data.service';
import { map, first, tap, mergeMap, shareReplay, retryWhen, delayWhen } from 'rxjs/operators';
import * as _ from 'lodash';
import { FormUtility } from './form-utility';

// This service can be used to set the select list items for use with the forms framework
@Injectable({
  providedIn: 'root',
})
export class SelectListService {
  private selectLists: BehaviorSubject<ISelectOptionGroup[]>[] = [];

  constructor(private referenceDataService: ReferenceDataService) {}

  getSelectList(
    selectListName: string,
    nameFormatter?: (data: object) => string,
  ): Observable<ISelectOptionGroup[]> {
    return this.ensureDataLoaded(selectListName).pipe(
      mergeMap(listLoaded => {
        if (listLoaded) {
          const list = this.selectLists[selectListName] as BehaviorSubject<ISelectOptionGroup[]>;
          if (nameFormatter) {
            return list.pipe(
              first(response => response != null),
              map(response =>
                _.map(response, group => {
                  group.options = _.map(group.options, option => {
                    option.name = nameFormatter(option.data);
                    return option;
                  });
                  return group;
                }),
              ),
            );
          } else {
            return list;
          }
        } else {
          throwError('Unable to load selectList for name: ' + selectListName);
        }
      }),
    );
  }

  getNumericOptionsList(
    selectListName: string,
    start: number,
    end: number,
  ): BehaviorSubject<ISelectOptionGroup[]> {
    const numOptions = new Array<ISelectOption>();

    if (start < end) {
      for (; start <= end; start++) {
        numOptions.push(this.createNumericOption(start));
      }
    } else {
      for (; start >= end; start--) {
        numOptions.push(this.createNumericOption(start));
      }
    }

    if (!this.selectLists[selectListName]) {
      this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);
    }

    this.registerSelectListOptions(selectListName, [
      {
        name: selectListName,
        options: numOptions,
        sortOrder: 1,
      },
    ]);

    return this.selectLists[selectListName];
  }

  getSelectListWithOptionValuesRemoved(
    selectListName: string,
    optionValuesToRemove: any[],
    nameFormatter?: (data: object) => string,
  ): Observable<ISelectOptionGroup[]> {
    return this.ensureDataLoaded(selectListName).pipe(
      mergeMap(listLoaded => {
        if (listLoaded) {
          return (this.selectLists[selectListName] as BehaviorSubject<ISelectOptionGroup[]>).pipe(
            map((groups: ISelectOptionGroup[]) => {
              const newGroups: ISelectOptionGroup[] = [];
              if (groups) {
                groups.forEach(group => {
                  const newGroup: ISelectOptionGroup = {
                    name: group.name,
                    options: group.options
                      .filter(option => optionValuesToRemove.indexOf(option.value) === -1)
                      .map(option => {
                        option.name = nameFormatter ? nameFormatter(option.data) : option.name;
                        return option;
                      }),
                    sortOrder: group.sortOrder,
                  };
                  newGroups.push(newGroup);
                });
              }
              return newGroups;
            }),
          );
        } else {
          throwError('Unable to load selectList for name: ' + selectListName);
        }
      }),
    );
  }

  registerSelectListOptions(selectListName: string, options: ISelectOptionGroup[]) {
    if (this.selectLists[selectListName] == null) {
      this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);
    }

    this.selectLists[selectListName].next(options);
  }

  getCurrentSelection(flatOptions: ISelectOption[], value: any): ISelectOption[] {
    if (value && Array.isArray(value)) {
      const result = [];

      for (const val of value) {
        const foundOption = flatOptions.find(option => option.value === val);

        if (foundOption) {
          result.push(foundOption);
        }
      }

      return result;
    } else {
      const currentOption = flatOptions.find(option => option.value === value);

      if (currentOption) {
        return [currentOption];
      }

      return [];
    }
  }

  flattenOptions(optionGroups: ISelectOptionGroup[]): ISelectOption[] {
    if (optionGroups != null) {
      const flatOptions = [];

      for (const optionGroup of optionGroups) {
        for (const option of optionGroup.options) {
          flatOptions.push(option);
        }
      }

      return flatOptions;
    }

    return [];
  }

  filterAndSortSelectOptionGroup(
    optionGroups: ISelectOptionGroup[],
    initialGroupFilter = '',
  ): ISelectOptionGroup[] {
    let result: ISelectOptionGroup[] = [];

    for (const group of optionGroups) {
      result.push(<ISelectOptionGroup>{
        name: group.name,
        sortOrder: group.sortOrder,
        options: this.getFilteredOptions(group.options),
      });
    }

    if (initialGroupFilter && result.findIndex(og => og.name === initialGroupFilter) !== -1) {
      result = result.filter(og => og.name === initialGroupFilter);
    }

    return result.sort((group1, group2) => {
      if (group1.sortOrder > group2.sortOrder) {
        return 1;
      } else if (group1.sortOrder < group2.sortOrder) {
        return -1;
      }
      return 0;
    });
  }

  private createNumericOption(num: number): ISelectOption {
    const opt: ISelectOption = {
      name: num.toString(),
      value: num,
      sortOrder: num,
      isDeleted: false,
    };
    return opt;
  }

  private ensureDataLoaded(selectListName: string): Observable<boolean> {
    if (selectListName == null) {
      console.warn(
        'You are missing an annotation in your DTO. Add [FieldMetadata(OptionList = "YourOptionList")] to the field you are trying to populate the list for.',
      );
      return of(false);
    }
    if (this.selectLists[selectListName] != null) {
      return of(true);
    }

    switch (selectListName) {
      case 'yesNoOptions':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);
        this.registerSelectListOptions(selectListName, FormUtility.getYesNoOptions());
        return of(true);
      case 'internalExternalOptions':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        this.registerSelectListOptions(selectListName, FormUtility.getInternalExternalOptions());
        return of(true);
      case 'userInputOptions':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        this.registerSelectListOptions(selectListName, FormUtility.getUserInputOptions());
        return of(true);
      case 'priorityOptions':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        this.registerSelectListOptions(selectListName, FormUtility.getPriorityOptions());
        return of(true);
      case 'isOptInEmailNotification':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        this.registerSelectListOptions(
          selectListName,
          FormUtility.getIsOptInEmailNotificationOptions(),
        );
        return of(true);
      case 'activeInactiveOptions':
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        this.registerSelectListOptions(selectListName, FormUtility.getActiveInactiveOptions());
        return of(true);
      default: {
        this.selectLists[selectListName] = new BehaviorSubject<ISelectOptionGroup[]>(null);

        const refDataCall$ = this.referenceDataService.getReferenceData(selectListName).pipe(
          tap(options => this.registerSelectListOptions(selectListName, options)),
          mergeMap(list => {
            if (list) {
              return of(true);
            } else {
              throwError(new Error('Unable to load reference list'));
            }
          }),
          shareReplay(1),
          retryWhen(errors =>
            errors.pipe(
              // retry in 5 seconds if an error occured getting the reference data
              delayWhen(() => timer(5000)),
            ),
          ),
        );

        refDataCall$.subscribe();

        return refDataCall$;
      }
    }
  }

  private getFilteredOptions(options: ISelectOption[]): ISelectOption[] {
    if (!options) {
      return null;
    }

    // show active items
    return options.filter(item => !item.isDeleted);
  }
}
